"use client";

import type { LexicalEditor } from "lexical";
import { $getNearestNodeFromDOMNode, $getNodeByKey } from "lexical";
import { $isListItemNode, $isListNode } from "@lexical/list";

import { findClosestBlockNodeKey } from "../../kernel/helpers/findClosestBlockNodeKey";
import type { BlockHighlightRect } from "./blockHighlightRect";

/** 手柄命中时向右探测，用于从 Grip 处取到块内容 DOM（勿扩大矩形条带：会与「深度相同取面积更小」比较冲突，易误选窄段落） */
const BLOCK_HANDLE_VIEWPORT_PROBE_DX = 40;

export type BlockHandlePos = { top: number; left: number; visible: boolean };

/**
 * 用 DOM 命中块（viewport 坐标）。作矩形命中的补充：左侧留白/手柄条带、或条带漏判时用 elementFromPoint（含手柄向右探测）。
 */
export function pickBlockKeyAtViewportPoint(
  clientX: number,
  clientY: number,
  editor: LexicalEditor,
  container: HTMLElement,
): string | null {
  let el = document.elementFromPoint(clientX, clientY);
  if (!el || !container.contains(el)) return null;
  // 指针落在块手柄（Grip）上时 elementFromPoint 命中 data-block-handle-region，原逻辑直接放弃会导致
  // hoverBlockKey 无法指向当前块；向右探测一行像素以命中块内容 DOM。
  if (el.closest("[data-block-handle-region]")) {
    const probe = document.elementFromPoint(clientX + BLOCK_HANDLE_VIEWPORT_PROBE_DX, clientY);
    if (!probe || !container.contains(probe)) return null;
    el = probe;
  }

  let key: string | null = null;
  // 必须用 editor.read：$getNearestNodeFromDOMNode 依赖 getActiveEditor（DOM 上 __lexicalKey_*），
  // editor.getEditorState().read 不会设置 activeEditor，会导致抛错或无法解析节点。
  editor.read(() => {
    const node = $getNearestNodeFromDOMNode(el);
    key = findClosestBlockNodeKey(node);
  });
  return key;
}

/**
 * 指针在容器内坐标（含 scroll）下命中块：竖直条带内可命中多个嵌套 li，取 ListItem 树深度最大者。
 */
export function pickBlockKeyAtContainerPoint(
  pointerX: number,
  pointerY: number,
  sortedRectsTopToBottom: BlockHighlightRect[],
  editor: LexicalEditor,
): string | null {
  const inside = sortedRectsTopToBottom.filter(
    (r) =>
      pointerY >= r.top &&
      pointerY <= r.top + r.height &&
      pointerX >= r.left &&
      pointerX <= r.left + r.width,
  );
  if (inside.length === 0) return null;

  let bestKey: string | null = null;
  let bestDepth = -1;
  let bestArea = Infinity;

  editor.getEditorState().read(() => {
    for (const r of inside) {
      const n = $getNodeByKey(r.key);
      if (!n) continue;
      let depth = 0;
      let p: typeof n | null = n;
      while (p) {
        if ($isListItemNode(p)) depth += 1;
        p = p.getParent();
      }
      const area = r.width * r.height;
      if (depth > bestDepth || (depth === bestDepth && area < bestArea)) {
        bestDepth = depth;
        bestArea = area;
        bestKey = r.key;
      }
    }
  });

  return bestKey;
}

/** 与 useBlockHandleOverlayState 一致：手柄贴在块左侧，列表扣掉项目符号缩进 */
export function computeHandlePositionForKey(
  key: string,
  editor: LexicalEditor,
  container: HTMLDivElement,
): BlockHandlePos | null {
  const el = editor.getElementByKey(key);
  if (!el) return null;

  const cRect = container.getBoundingClientRect();
  const aRect = el.getBoundingClientRect();
  const gap = 4;
  const handleWidth = 20;

  let listBulletOffset = 0;
  editor.getEditorState().read(() => {
    const node = $getNodeByKey(key);
    if ($isListItemNode(node)) {
      let depth = 0;
      let p: ReturnType<typeof $getNodeByKey> = node?.getParent() ?? null;
      let isOrdered = false;
      if ($isListNode(p)) isOrdered = p.getTag?.() === "ol";
      while (p) {
        if ($isListItemNode(p)) depth += 1;
        p = p.getParent();
      }
      listBulletOffset = (isOrdered ? 32 : 20) + depth * 24;
    }
  });

  const top = aRect.top - cRect.top + container.scrollTop;
  const left = aRect.left - cRect.left + container.scrollLeft - (handleWidth + gap + listBulletOffset);

  return { top, left, visible: true };
}

/** 拖拽菜单 `fixed` 锚点（viewport），与 useBlockHandleOverlayState.recompute 中 setDragMenuPos 一致 */
export function computeDragMenuViewportAnchorForKey(
  key: string,
  editor: LexicalEditor,
  container: HTMLDivElement,
): { top: number; left: number } | null {
  const el = editor.getElementByKey(key);
  if (!el) return null;
  const cRect = container.getBoundingClientRect();
  const aRect = el.getBoundingClientRect();
  const gap = 4;
  const handleWidth = 20;
  let listBulletOffset = 0;
  editor.getEditorState().read(() => {
    const node = $getNodeByKey(key);
    if ($isListItemNode(node)) {
      let depth = 0;
      let p: ReturnType<typeof $getNodeByKey> = node?.getParent() ?? null;
      let isOrdered = false;
      if ($isListNode(p)) isOrdered = p.getTag?.() === "ol";
      while (p) {
        if ($isListItemNode(p)) depth += 1;
        p = p.getParent();
      }
      listBulletOffset = (isOrdered ? 32 : 20) + depth * 24;
    }
  });
  const handleLeft = aRect.left - cRect.left + container.scrollLeft - (handleWidth + gap + listBulletOffset);
  return {
    top: aRect.top,
    left: cRect.left + handleLeft - container.scrollLeft,
  };
}
